// Apolo IDE — Learning Loop L6: useLearning hook.
//
// Thin React wrapper that pulls candidates / skills / settings from the
// Worker via `apiFetch`. Mutations (approve, reject, dismiss) live as
// named exports so non-React callers (vaultCommit, tests) can reuse them.
//
// The hook keeps a 30s module-level cache to absorb the panel's polling
// refresh cycle without re-fetching while data is still fresh.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';

const TTL_MS = 30_000;
const cache = new Map();

function key(scope, args) {
  return `${scope}::${JSON.stringify(args || {})}`;
}

export function clearLearningCache() {
  cache.clear();
}

async function getCached(k, fetcher, { bypass = false } = {}) {
  if (!bypass) {
    const hit = cache.get(k);
    if (hit && hit.expiresAt > Date.now()) return hit.value;
  }
  const value = await fetcher();
  cache.set(k, { value, expiresAt: Date.now() + TTL_MS });
  return value;
}

export async function approveCandidate(id) {
  return apiFetch(`/api/learning/candidates/${id}/approve`, { method: 'POST' });
}

export async function rejectCandidate(id, reason = '') {
  return apiFetch(`/api/learning/candidates/${id}`, {
    method: 'PATCH',
    body: { status: 'rejected', reason },
  });
}

export async function dismissCandidate(id, reason = '') {
  return apiFetch(`/api/learning/candidates/${id}`, {
    method: 'PATCH',
    body: { status: 'dismissed', reason },
  });
}

export async function fetchCandidates(status = 'pending', { page = 0, limit = 20 } = {}) {
  return apiFetch(
    `/api/learning/candidates?status=${encodeURIComponent(status)}&page=${page}&limit=${limit}`,
  );
}

export async function fetchSkills() {
  return apiFetch('/api/learning/skills');
}

export async function fetchNudges(status = 'pending') {
  return apiFetch(`/api/learning/nudges?status=${encodeURIComponent(status)}`);
}

export async function fetchSettings() {
  return apiFetch('/api/learning/settings');
}

export async function updateSettings(payload) {
  return apiFetch('/api/learning/settings', { method: 'PUT', body: payload });
}

export async function toggleSkillStatus(skillId, nextStatus) {
  const verb = nextStatus === 'active' ? 'enable' : 'disable';
  return apiFetch(`/api/learning/skills/${skillId}/${verb}`, { method: 'POST' });
}

// ───────────────────────────────────────────────────────────────
// L7 — Outcome Tracker surface
// ───────────────────────────────────────────────────────────────

export async function fetchMetrics({ days = 7 } = {}) {
  return apiFetch(`/api/learning/metrics?days=${days}`);
}

export async function recordSkillOutcome({
  skill_id,
  session_id,
  outcome,
  signal_source = 'manual',
  metadata = {},
}) {
  return apiFetch('/api/learning/outcomes', {
    method: 'POST',
    body: { skill_id, session_id, outcome, signal_source, metadata },
  });
}

// ───────────────────────────────────────────────────────────────
// LRN5 — live status + kill-switch
// ───────────────────────────────────────────────────────────────

// The full /status payload flows through verbatim (the poller setData's the whole
// value — no field pick), so consumers read it directly. liveness-feed E1 added
// `feed: { verdict, input_count, output_recent, last_candidate_at }` to that payload;
// Status.jsx reads `status.feed` with no extra fetch (E2-WS1-T02).
export async function fetchLearningStatus() {
  return apiFetch('/api/learning/status');
}

export async function setKillSwitch(enabled, scope = 'user') {
  return apiFetch('/api/learning/kill-switch', { method: 'POST', body: { enabled, scope } });
}

// ───────────────────────────────────────────────────────────────
// liveness-tier-s E7 — breaker recovery surface
// ───────────────────────────────────────────────────────────────

export async function fetchBreakerStatus() {
  return apiFetch('/api/learning/breaker-status');
}

export async function fetchBreakerEvents(limit = 10) {
  return apiFetch(`/api/learning/breaker-events?limit=${limit}`);
}

// Clears the GLOBAL breaker (kill_switch + promotion_paused). The Worker runs a
// dry-run first and returns 409 { would_re_arm, reasons } if it would re-arm now.
export async function clearBreaker() {
  return apiFetch('/api/learning/kill-switch', { method: 'POST', body: { enabled: false, scope: 'global' } });
}

function makeUseList(scope, fetcher, defaultArgs) {
  return function useList(args = defaultArgs, { enabled = true, pollIntervalMs = 0 } = {}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const argsRef = useRef(args);
    argsRef.current = args;

    const runFetch = useCallback(async (bypass = false) => {
      setLoading(true);
      setError(null);
      try {
        const value = await getCached(key(scope, argsRef.current), () => fetcher(argsRef.current), {
          bypass,
        });
        setData(value);
      } catch (e) {
        setError(e?.message || String(e));
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      if (!enabled) return undefined;
      runFetch(false);
      return undefined;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, runFetch, JSON.stringify(args || {})]);

    useEffect(() => {
      if (!enabled || !pollIntervalMs || pollIntervalMs <= 0) return undefined;
      // Skip polls while the tab/window is hidden — /api/learning/status runs the
      // heartbeat aggregates; polling it every N seconds in a backgrounded tab was
      // pure waste (and, before the 0105 indexes, a D1 row-read firehose). Resumes
      // (and refetches once) when the tab becomes visible again.
      const tick = () => {
        if (typeof document !== 'undefined' && document.hidden) return;
        runFetch(true);
      };
      const timer = setInterval(tick, pollIntervalMs);
      const onVisible = () => { if (!document.hidden) runFetch(true); };
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
      return () => {
        clearInterval(timer);
        if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
      };
    }, [enabled, pollIntervalMs, runFetch]);

    return { data, loading, error, refetch: useCallback(() => runFetch(true), [runFetch]) };
  };
}

const _useCandidates = makeUseList('candidates', (a) => fetchCandidates(a?.status, a), {
  status: 'pending',
});
export function useLearningCandidates(status = 'pending', opts = {}) {
  return _useCandidates({ status }, opts);
}

const _useSkills = makeUseList('skills', () => fetchSkills(), {});
export function useLearningSkills(opts = {}) {
  return _useSkills({}, opts);
}

const _useNudges = makeUseList('nudges', (a) => fetchNudges(a?.status), { status: 'pending' });
export function useLearningNudges(status = 'pending', opts = {}) {
  return _useNudges({ status }, opts);
}

const _useStatus = makeUseList('status', () => fetchLearningStatus(), {});
export function useLearningStatus(opts = {}) {
  return _useStatus({}, opts);
}

const _useBreakerStatus = makeUseList('breaker-status', () => fetchBreakerStatus(), {});
export function useBreakerStatus(opts = {}) {
  return _useBreakerStatus({}, opts);
}

const _useBreakerEvents = makeUseList('breaker-events', (a) => fetchBreakerEvents(a?.limit), { limit: 10 });
export function useBreakerEvents(limit = 10, opts = {}) {
  return _useBreakerEvents({ limit }, opts);
}
